import * as qs from 'qs'
import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import { useSourceTemplateIdParam } from '../../useSourceTemplateIdParam'

export type TemplateEditMode =
  | {
      // 通常の新規作成
      mode: 'new'
      sessionId: string
    }
  | {
      // ひな形テンプレートを使った新規作成
      mode: 'newFromPreparedTemplate'
      sessionId: string
      templateId: number
      key?: string
    }
  | {
      // 既存ひな形の編集
      mode: 'edit'
      templateId: number
    }

const TemplateEditModeContext = createContext<TemplateEditMode | null>(null)

/**
 * ひな形編集モードを提供するプロバイダー
 *
 * - 新規作成時は crypto.randomUUID() でセッションIDを生成
 *   - ひな形新規作成時にこのひな形限定の選択肢を TanStack Query で適切にキャッシュするために使用している
 * - 編集時は templateId を使用
 */
export const TemplateEditModeProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation()
  // ひな形テンプレートをもとにした新規作成かどうかを表すフラグ
  const isIndustry = location.search.includes('industry')
  // 新規作成時にはURLパラメータにidという値は存在しないため、0になる
  const sourceTemplateId = useSourceTemplateIdParam()
  // useState の初期化関数は初回レンダリング時のみ実行される
  const [sessionId] = useState(() => crypto.randomUUID())

  // URLパラメータからkeyを取得
  const searchParams = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })
  const keyParam = searchParams['key']
  const key = typeof keyParam === 'string' ? keyParam : undefined

  const templateEditMode = useMemo((): TemplateEditMode => {
    if (sourceTemplateId === 0) {
      return { mode: 'new', sessionId }
    }
    if (isIndustry) {
      return {
        mode: 'newFromPreparedTemplate',
        sessionId,
        templateId: sourceTemplateId,
        key,
      }
    }
    return { mode: 'edit', templateId: sourceTemplateId }
  }, [sourceTemplateId, isIndustry, sessionId, key])

  return (
    <TemplateEditModeContext.Provider value={templateEditMode}>
      {children}
    </TemplateEditModeContext.Provider>
  )
}

export const useTemplateEditMode = (): TemplateEditMode => {
  const context = useContext(TemplateEditModeContext)
  if (context === null) {
    throw new Error(
      'useTemplateEditMode must be used within TemplateEditModeProvider',
    )
  }
  return context
}
